import React, { useState, useEffect, useCallback } from "react";
import { Check, X } from "lucide-react";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import { usePermissions } from "../../../contexts/PermissionContext";
import { listLeaves, listLeaveTypesDropdown, approveLeave, rejectLeave } from "../../../services/hrService";
import { LeaveApplication, LeaveType } from "../../../services/hrTypes";

const LeaveApprovals: React.FC = () => {
    const { userId } = usePermissions();

    const [leaves, setLeaves] = useState<LeaveApplication[]>([]);
    const [types, setTypes] = useState<Pick<LeaveType, "id" | "name" | "code">[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [target, setTarget] = useState<LeaveApplication | null>(null);
    const [mode, setMode] = useState<"approve" | "reject">("approve");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [l, t] = await Promise.all([listLeaves(), listLeaveTypesDropdown()]);
            setLeaves(l.filter((x) => x.workflow_state === "submitted" && x.approver_user_id === userId));
            setTypes(t);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const openModal = (l: LeaveApplication, m: "approve" | "reject") => {
        setTarget(l);
        setMode(m);
        setNote("");
        setModalError(null);
    };

    const handleConfirm = async () => {
        if (!target) return;
        if (mode === "reject" && !note.trim()) {
            setModalError("A reason is required to reject.");
            return;
        }
        setBusy(true);
        setModalError(null);
        try {
            if (mode === "approve") await approveLeave(target.id, note || undefined);
            else await rejectLeave(target.id, note.trim());
            setToast({ type: "success", message: mode === "approve" ? "Leave approved" : "Leave rejected" });
            setTarget(null);
            fetchAll();
        } catch (err: any) {
            setModalError(err.response?.data?.detail || err.message || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    const typeName = (id: number) => types.find((t) => t.id === id)?.name || `Type #${id}`;
    const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>Leave Approvals</span>
            </div>

            <div className="netbody-content">
                <div className="table-list">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Applicant</th>
                                <th className="table-list-th">Leave Type</th>
                                <th className="table-list-th">From</th>
                                <th className="table-list-th">To</th>
                                <th className="table-list-th">Days</th>
                                <th className="table-list-th">Reason</th>
                                <th className="table-list-th" style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={["3xs", "sm", "md", "sm", "sm", "2xs", "md", { width: "icon", align: "right", count: 2 }]} />}
                            {!loading && leaves.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="table-list-cell">
                                        <NoDataFound title="Nothing to approve" message="No leave applications are awaiting your approval." />
                                    </td>
                                </tr>
                            )}
                            {!loading && leaves.map((l, idx) => (
                                <tr key={l.id} className="table-list-row" data-testid={`ROW_approval_${l.id}`}>
                                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                    <td className="table-list-cell"><span className="table-list-cell-title">User #{l.staff_user_id}</span></td>
                                    <td className="table-list-cell">{typeName(l.leave_type_id)}</td>
                                    <td className="table-list-cell">{fmtDate(l.from_date)}</td>
                                    <td className="table-list-cell">{fmtDate(l.to_date)}</td>
                                    <td className="table-list-cell">{l.days}</td>
                                    <td className="table-list-cell">{l.reason || "-"}</td>
                                    <td className="table-list-cell">
                                        <div className="table-list-actions" style={{ justifyContent: "flex-end" }}>
                                            <Button variant="success" icon={Check} onClick={() => openModal(l, "approve")} data-testid={`BTN_approve_${l.id}`} aria-label="Approve" />
                                            <Button variant="danger" icon={X} onClick={() => openModal(l, "reject")} data-testid={`BTN_reject_${l.id}`} aria-label="Reject" />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal isOpen={!!target} onClose={() => setTarget(null)} size="sm" data-testid="MOD_approval">
                <Modal.Header>{mode === "approve" ? "Approve Leave" : "Reject Leave"}</Modal.Header>
                <Modal.Body>
                    {modalError && <div className="form-error" style={{ marginBottom: 8 }}>{modalError}</div>}
                    <p style={{ fontSize: "0.8125rem", color: "var(--text-secondary)", marginBottom: 12 }}>
                        {target && `${typeName(target.leave_type_id)} · ${fmtDate(target.from_date)} → ${fmtDate(target.to_date)} · ${target.days} day(s)`}
                    </p>
                    <FormControl.Textarea
                        label={mode === "reject" ? "Reason" : "Note"}
                        required={mode === "reject"}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder={mode === "reject" ? "Why is this being rejected?" : "Optional note"}
                        data-testid="INP_approval_note"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="app-modal-footer-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
                        <Button variant="secondary-outline" onClick={() => setTarget(null)} disabled={busy} data-testid="BTN_approval_cancel">
                            Cancel
                        </Button>
                        <Button
                            variant={mode === "approve" ? "success" : "danger"}
                            icon={mode === "approve" ? Check : X}
                            onClick={handleConfirm}
                            disabled={busy}
                            data-testid="BTN_approval_confirm"
                        >
                            {busy ? "Working..." : mode === "approve" ? "Approve" : "Reject"}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default LeaveApprovals;
